"use client";

import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { Z_INDEX_VALUES } from "@/lib/constants/z-index";
import { isBrowser, isIOS } from "@/lib/utils/browser";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

// Interfaces for vendor-specific fullscreen methods
interface HTMLElementWithVendorFullscreen extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface DocumentWithVendorFullscreen extends Document {
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

interface UseFullscreenOptions {
  containerRef: RefObject<HTMLElement | null>;
  isMobile: boolean;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
}

interface UseFullscreenReturn {
  isFullscreen: boolean;
  isPseudoFullscreen: boolean;
  toggleFullscreen: () => Promise<void>;
}

/**
 * Delay in milliseconds to wait for smooth scroll animation to complete
 * before applying pseudo-fullscreen styles on mobile devices
 */
const SCROLL_ANIMATION_DELAY_MS = 300;

/**
 * Custom hook for handling fullscreen functionality
 * Supports both native fullscreen API and iOS pseudo-fullscreen mode
 */
export function useFullscreen({
  containerRef,
  isMobile,
  onFullscreenToggle,
}: UseFullscreenOptions): UseFullscreenReturn {
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  // iOS detection is only used to decide which fullscreen strategy to use (native vs pseudo).
  // We compute it lazily so it's available *before the first user interaction* on the client.
  // (This value is not used to render UI, so it won't cause SSR hydration mismatches.)
  const [isIOSDevice] = useState(() => (isBrowser() ? isIOS() : false));
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const isPseudoFullscreenRef = useRef(false);
  const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useBodyScrollLock();

  // Dispatch custom event for pseudo-fullscreen state changes
  const dispatchPseudoFullscreenChange = useCallback((isFullscreen: boolean) => {
    if (isBrowser()) {
      window.dispatchEvent(new CustomEvent("pseudofullscreenchange", { detail: { isFullscreen } }));
    }
  }, []);

  // Handle iOS pseudo-fullscreen
  const togglePseudoFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    // Clear any pending timeout FIRST to prevent race conditions on rapid toggles
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const newState = !isPseudoFullscreen;

    // When entering fullscreen on mobile, scroll to position the game at the top of viewport
    if (newState && isMobile) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const currentScrollY = window.scrollY;
      const targetScrollY = currentScrollY + containerRect.top;

      // Scroll to align the top of the game container with the top of the viewport
      window.scrollTo({ top: targetScrollY, behavior: "smooth" });

      // Wait for scroll to complete before applying fullscreen styles
      timeoutRef.current = setTimeout(() => {
        // Only execute if component is still mounted
        if (!mountedRef.current) {
          return;
        }

        setIsPseudoFullscreen(true);
        isPseudoFullscreenRef.current = true;
        lockBodyScroll();
        dispatchPseudoFullscreenChange(true);
        onFullscreenToggle?.(true);
        timeoutRef.current = null;
      }, SCROLL_ANIMATION_DELAY_MS); // Allow time for smooth scroll animation
      return;
    }

    setIsPseudoFullscreen(newState);
    isPseudoFullscreenRef.current = newState;

    // Prevent body scroll when in pseudo-fullscreen
    if (newState) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    dispatchPseudoFullscreenChange(newState);
    onFullscreenToggle?.(newState);
  }, [
    isPseudoFullscreen,
    isMobile,
    containerRef,
    dispatchPseudoFullscreenChange,
    onFullscreenToggle,
    lockBodyScroll,
    unlockBodyScroll,
  ]);

  // Method to toggle fullscreen (native or pseudo)
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Fullscreen API handling requires complex browser compatibility logic
  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    // Fullscreen is desktop-only; no-op on mobile.
    if (isMobile) return;

    // iOS doesn't support Fullscreen API for arbitrary elements
    if (isIOSDevice) {
      togglePseudoFullscreen();
      return;
    }

    if (!document.fullscreenElement) {
      try {
        const element = containerRef.current as HTMLElementWithVendorFullscreen;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
        onFullscreenToggle?.(true);
      } catch (error) {
        console.error("Error attempting to enable fullscreen:", error);
      }
    } else {
      try {
        const doc = document as DocumentWithVendorFullscreen;
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
        onFullscreenToggle?.(false);
      } catch (error) {
        console.error("Error attempting to exit fullscreen:", error);
      }
    }
  }, [isMobile, isIOSDevice, togglePseudoFullscreen, containerRef, onFullscreenToggle]);

  // Listen for native fullscreen changes and sync pseudo-fullscreen state
  useEffect(() => {
    if (!isBrowser()) return;

    const handleFullscreenChange = () => {
      const nativeFullscreen = isBrowser() && !!document.fullscreenElement;
      setIsNativeFullscreen(nativeFullscreen);
      // If native fullscreen exits, also exit pseudo-fullscreen if active
      if (!nativeFullscreen && isPseudoFullscreen && !isIOSDevice) {
        setIsPseudoFullscreen(false);
        isPseudoFullscreenRef.current = false;
        unlockBodyScroll();
        dispatchPseudoFullscreenChange(false);
        onFullscreenToggle?.(false);
      }
    };

    // Initialize native fullscreen state on mount
    setIsNativeFullscreen(!!document.fullscreenElement);

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [
    isPseudoFullscreen,
    isIOSDevice,
    dispatchPseudoFullscreenChange,
    onFullscreenToggle,
    unlockBodyScroll,
  ]);

  // Mount/unmount effect - handles component lifecycle
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      // Clear any pending timeout FIRST to prevent callback execution
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Restore body scroll if still in pseudo-fullscreen on unmount
      if (isPseudoFullscreenRef.current) {
        unlockBodyScroll();
      }

      // Then mark as unmounted (safety net for any in-flight callbacks)
      mountedRef.current = false;
    };
  }, [unlockBodyScroll]); // Only unlockBodyScroll in deps - effect runs on mount/unmount only

  // Determine if currently in fullscreen (native or pseudo)
  const isFullscreen = isPseudoFullscreen || isNativeFullscreen;

  useEffect(() => {
    // fixes posthog recording issues when in fullscreen
    // changing dom elements position to fixed and z-index to 9999 fixes allows
    // posthog to detect changes to the dom elements when in fullscreen
    // per conversation with posthog support ticket
    const el = containerRef.current;
    if (!el || !isFullscreen) return;

    el.style.position = "fixed";
    el.style.inset = "0";
    el.style.zIndex = Z_INDEX_VALUES.FULLSCREEN_CONTAINER.toString();
    return () => {
      el.style.position = "";
      el.style.inset = "";
      el.style.zIndex = "";
    };
  }, [isFullscreen, containerRef]);

  return {
    isFullscreen,
    isPseudoFullscreen,
    toggleFullscreen,
  };
}
